import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';

const ApprovalTermsModal = ({ isOpen, onClose, client, onApprove }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    funding_amount: '',
    factor_rate: '1.25',
    payment_frequency: 'daily',
    term_length: '',
    payment_amount: '',
    notes: ''
  });

  const [payback, setPayback] = useState(0);
  const [loading, setLoading] = useState(false);

  // Auto-calculate payback when funding amount or factor rate changes
  useEffect(() => {
    const amount = parseFloat(formData.funding_amount) || 0;
    const factor = parseFloat(formData.factor_rate) || 1;
    const calculated = amount * factor;
    setPayback(calculated);

    // Auto-calculate payment amount if term length is provided
    if (formData.term_length && calculated > 0) {
      const termLength = parseInt(formData.term_length) || 1;
      const paymentAmt = calculated / termLength;
      setFormData(prev => ({
        ...prev,
        payment_amount: paymentAmt.toFixed(2)
      }));
    }
  }, [formData.funding_amount, formData.factor_rate, formData.term_length]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validate with toasts (alert() is silently blocked in some async contexts)
    if (!formData.funding_amount || parseFloat(formData.funding_amount) <= 0) {
      toast({ title: 'Missing field', description: 'Funding Amount is required', variant: 'destructive' });
      return;
    }
    if (!formData.factor_rate || parseFloat(formData.factor_rate) <= 0) {
      toast({ title: 'Missing field', description: 'Factor Rate is required', variant: 'destructive' });
      return;
    }
    if (!formData.term_length || parseInt(formData.term_length) <= 0) {
      toast({ title: 'Missing field', description: 'Term Length is required', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const termsData = {
        ...formData,
        payback_amount: payback,
        business_name: client?.name || client?.business_name || ''
      };
      await onApprove(termsData);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Approve Deal - Enter Terms</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <p className="text-sm text-blue-800">
            <strong>Client:</strong> {client?.name}
          </p>
          <p className="text-sm text-blue-800 mt-1">
            Entering terms will approve this deal and move it to Submissions.
          </p>
        </div>

        <div className="space-y-4">
          {/* Funding Amount */}
          <div>
            <label className="block text-sm font-medium mb-1">Funding Amount *</label>
            <input
              type="number"
              value={formData.funding_amount}
              onChange={(e) => handleChange('funding_amount', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="50000"
            />
          </div>

          {/* Factor Rate */}
          <div>
            <label className="block text-sm font-medium mb-1">Factor Rate *</label>
            <input
              type="number"
              step="0.01"
              value={formData.factor_rate}
              onChange={(e) => handleChange('factor_rate', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="1.25"
            />
            <p className="text-xs text-gray-500 mt-1">e.g., 1.25 = 125% payback</p>
          </div>

          {/* Calculated Payback */}
          <div className="bg-green-50 p-4 rounded">
            <label className="block text-sm font-medium mb-1">Calculated Payback</label>
            <p className="text-2xl font-bold text-green-700">
              ${payback.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>

          {/* Payment Frequency */}
          <div>
            <label className="block text-sm font-medium mb-1">Payment Frequency *</label>
            <select
              value={formData.payment_frequency}
              onChange={(e) => handleChange('payment_frequency', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          {/* Term Length */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Term Length ({formData.payment_frequency === 'daily' ? 'Days' : 'Weeks'}) *
            </label>
            <input
              type="number"
              value={formData.term_length}
              onChange={(e) => handleChange('term_length', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder={formData.payment_frequency === 'daily' ? '110' : '24'}
            />
          </div>

          {/* Payment Amount */}
          <div>
            <label className="block text-sm font-medium mb-1">Payment Amount *</label>
            <input
              type="number"
              step="0.01"
              value={formData.payment_amount}
              onChange={(e) => handleChange('payment_amount', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Auto-calculated"
            />
            <p className="text-xs text-gray-500 mt-1">Auto-calculated but can be adjusted</p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows="3"
              placeholder="Additional notes about the deal"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            disabled={loading}
            className="flex-1 bg-green-600 hover:bg-green-700"
            type="submit"
            data-testid="approve-terms-submit-btn"
          >
            {loading ? 'Approving...' : 'Approve & Move to Submissions'}
          </Button>
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
        </form>
      </div>
    </div>
  );
};

export default ApprovalTermsModal;
